import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
    FormControl,
    FormLabel,
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
} from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { useLazyListUsersForExportQuery } from "store/services/userApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { FileDownloadOutlined } from "@mui/icons-material";

const USER_ROLES = [
    "System Admin",
    "Account Admin",
    "Entity Admin",
    "System User",
    "Account User",
    "Entity User",
    "System Manager",
    "Account Manager",
    "Entity Manager",
];

const EXPORT_DATA_INITIAL_STATE = {
    selectedRoles: [],
    allRoles: false,
};

const ExportUsers = ({ isBusy = false }) => {
    const [showDialog, setShowDialog] = React.useState(false);
    const {
        handleSubmit,
        reset,
        watch,
        setValue,
    } = useForm({
        defaultValues: EXPORT_DATA_INITIAL_STATE,
    });

    const allRoles = watch("allRoles");
    const selectedRoles = watch("selectedRoles");

    const [fetchUsers, { isLoading: isFetching }] = useLazyListUsersForExportQuery();

    const handleDownload = async (data) => {
        try {
            let params = {};

            // Add roles filter if not all roles selected
            if (!data.allRoles && data.selectedRoles.length > 0) {
                params.roles = data.selectedRoles.join(',');
            }

            const responseData = await fetchUsers(params).unwrap();

            if (!responseData?.data || responseData.data.length === 0) {
                toast.info("No users found for the selected criteria.");
                return;
            }

            const formattedData = responseData.data.map((user, index) => {
                // Extract all unique roles
                const allUserRoles = user.roles
                    ?.flat()
                    .map(auth => auth?.role)
                    .filter(Boolean);
                const uniqueRoles = [...new Set(allUserRoles)];

                // Extract all unique account names
                const accountNames = user.accounts
                    ?.map(acc => acc?.name)
                    .filter(Boolean);
                const uniqueAccounts = [...new Set(accountNames)];

                // Extract all unique entity names
                const entityNames = user.entities
                    ?.map(ent => ent?.displayName || ent?.name)
                    .filter(Boolean);
                const uniqueEntities = [...new Set(entityNames)];

                return {
                    SN: index + 1,
                    Name: user.name || "N/A",
                    Email: user.email || "N/A",
                    Role: uniqueRoles.join(", ") || "N/A",
                    Account: uniqueAccounts.join(", ") || "N/A",
                    Entity: uniqueEntities.join(", ") || "N/A",
                };
            });

            const fileName = `Users_Export_${new Date().toISOString().split('T')[0]}`;

            exportExcelFile({
                sheetData: formattedData,
                fileName: fileName,
            });

            toast.success("Users exported successfully");
            handleReset();
        } catch (error) {
            toast.error("Error exporting users: " + (error.message || "Unknown error"));
        }
    };

    const handleReset = () => {
        setShowDialog(false);
        reset(EXPORT_DATA_INITIAL_STATE);
    };

    const handleAllRolesChange = (checked) => {
        setValue("allRoles", checked);
        if (checked) {
            setValue("selectedRoles", []);
        }
    };

    const handleRoleChange = (role, checked) => {
        const currentRoles = selectedRoles || [];
        if (checked) {
            setValue("selectedRoles", [...currentRoles, role]);
            setValue("allRoles", false);
        } else {
            setValue("selectedRoles", currentRoles.filter(r => r !== role));
        }
    };

    return (
        <>
            <FileDownloadOutlined
                color="success"
                onClick={() => !isBusy && setShowDialog(true)}
                style={{ cursor: isBusy ? 'not-allowed' : 'pointer' }}
            />

            <AppConfirmDialog
                open={showDialog}
                onClose={handleReset}
                onConfirm={handleSubmit(handleDownload)}
                title="Export Users to Excel"
                dialogIcon={() => <FileDownloadOutlined color="success" />}
                confirmText="Download"
                confirmDisabled={isFetching || isBusy}
            >
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Filter by Role</FormLabel>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allRoles}
                                    onChange={(e) => handleAllRolesChange(e.target.checked)}
                                />
                            }
                            label="All Roles"
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {USER_ROLES.map((role) => (
                                <FormControlLabel
                                    key={role}
                                    control={
                                        <Checkbox
                                            checked={selectedRoles?.includes(role) || false}
                                            onChange={(e) => handleRoleChange(role, e.target.checked)}
                                            disabled={allRoles}
                                        />
                                    }
                                    label={role}
                                />
                            ))}
                        </Box>
                    </FormGroup>
                </FormControl>
            </AppConfirmDialog>
        </>
    );
};

export default ExportUsers;